/**
 * @typedef {Object} ProductionOrderCreateDto
 * @property {string} orderNumber
 * @property {string} [orderType]
 * @property {number} statusId
 * @property {number} warehouseId
 * @property {string} [plannedStartDate]
 * @property {string} [plannedEndDate]
 * @property {string} [actualStartDate]
 * @property {string} [actualEndDate]
 * @property {string} [externalSystemCode]
 * @property {string} [externalOrderNumber]
 * @property {string} [externalReference]
 * @property {string} [notes]
 * @property {boolean} [isInternal=false] - Planned/internal order, immutable after creation
 */

/**
 * @typedef {Object} ProductionOrderUpdateDto
 * @property {string} [orderNumber]
 * @property {string} [orderType]
 * @property {number} [statusId]
 * @property {number} [warehouseId]
 * @property {string} [plannedStartDate]
 * @property {string} [plannedEndDate]
 * @property {string} [actualStartDate]
 * @property {string} [actualEndDate]
 * @property {string} [externalSystemCode]
 * @property {string} [externalOrderNumber]
 * @property {string} [externalReference]
 * @property {string} [notes]
 * @property {boolean} [isActive]
 */

/**
 * @typedef {Object} ProductionOrderResponseDto
 * @property {number} productionOrderId
 * @property {string} orgId
 * @property {string} orderNumber
 * @property {string} [orderType]
 * @property {number} statusId
 * @property {string} statusName
 * @property {number} warehouseId
 * @property {string} warehouseName
 * @property {string} [plannedStartDate]
 * @property {string} [plannedEndDate]
 * @property {string} [actualStartDate]
 * @property {string} [actualEndDate]
 * @property {string} [externalSystemCode]
 * @property {string} [externalOrderNumber]
 * @property {string} [externalReference]
 * @property {string} [notes]
 * @property {boolean} isActive
 * @property {boolean} isInternal
 * @property {string} createdDate
 * @property {string} [createdBy]
 * @property {string} [modifiedDate]
 * @property {string} [modifiedBy]
 * @property {number} lineCount
 */

/**
 * @typedef {Object} ProductionOrderListResponse
 * @property {ProductionOrderResponseDto[]} orders
 * @property {number} totalCount
 * @property {number} pageNumber
 * @property {number} pageSize
 * @property {number} totalPages
 */

/**
 * @typedef {Object} ProductionOrderLineCreateDto
 * @property {number} componentId
 * @property {number} recipeVersionId
 * @property {number} routeId
 * @property {number} quantityOrdered
 */

/**
 * @typedef {Object} ProductionOrderLineUpdateDto
 * @property {number} [componentId]
 * @property {number} [recipeVersionId]
 * @property {number} [routeId]
 * @property {number} [quantityOrdered]
 * @property {number} [quantityCompleted]
 */

/**
 * @typedef {Object} ProductionOrderLineResponseDto
 * @property {number} orderLineId
 * @property {number} productionOrderId
 * @property {number} lineNumber
 * @property {number} componentId
 * @property {string} componentName
 * @property {string} componentCode
 * @property {number} recipeVersionId
 * @property {string} recipeVersionName
 * @property {number} routeId
 * @property {string} routeName
 * @property {number} quantityOrdered
 * @property {number} quantityCompleted
 * @property {boolean} isActive
 * @property {string} createdDate
 * @property {string} [createdBy]
 * @property {string} [modifiedDate]
 * @property {string} [modifiedBy]
 */

/**
 * @typedef {Object} SuggestedBoxDto
 * @property {number} boxId
 * @property {string} componentName
 * @property {string} boxTypeName
 * @property {number} [length]
 * @property {number} [width]
 * @property {number} [height]
 * @property {string} [dimensionUnit]
 * @property {number} quantityOnHand
 * @property {number} [boxVolume]
 * @property {number} [requiredVolume]
 * @property {boolean} fitsAllItems
 * @property {boolean} isSuggested
 */

/**
 * @typedef {Object} ProductionOrderStatusDto
 * @property {number} statusId
 * @property {string} statusName
 * @property {string} statusCode
 */

const BASE_PATH = 'v1/ProductionOrder';

export default function (instance) {
  return {
    /**
     * Get all production orders with pagination
     * @param {Object} [params]
     * @param {boolean} [params.includeInactive=false]
     * @param {number} [params.pageNumber=1]
     * @param {number} [params.pageSize=50]
     * @returns {Promise<{data: ProductionOrderListResponse}>}
     */
    getOrders(params = {}) {
      return instance.get(BASE_PATH, { params });
    },

    /**
     * Get production order by ID
     * @param {number} id
     * @returns {Promise<{data: ProductionOrderResponseDto}>}
     */
    getOrder(id) {
      return instance.get(`${BASE_PATH}/${id}`);
    },

    /**
     * Create new production order
     * @param {ProductionOrderCreateDto} payload
     * @returns {Promise<{data: ProductionOrderResponseDto}>}
     */
    createOrder(payload) {
      return instance.post(BASE_PATH, payload);
    },

    /**
     * Update production order
     * @param {number} id
     * @param {ProductionOrderUpdateDto} payload
     * @returns {Promise<{data: ProductionOrderResponseDto}>}
     */
    updateOrder(id, payload) {
      return instance.put(`${BASE_PATH}/${id}`, payload);
    },

    /**
     * Delete production order (soft delete)
     * @param {number} id
     * @returns {Promise<void>}
     */
    deleteOrder(id) {
      return instance.delete(`${BASE_PATH}/${id}`);
    },

    /**
     * Cancel a production order - atomically cancels every open WIP item
     * under it (releasing reserved inventory), cancels its lines, then
     * marks the order Cancelled
     * @param {number} id
     * @returns {Promise<{data: ProductionOrderResponseDto}>}
     */
    cancelOrder(id) {
      return instance.post(`${BASE_PATH}/${id}/cancel`);
    },

    /**
     * Plan a production order - sets its planned start/end date and time,
     * moves it to Planned
     * @param {number} id
     * @param {{plannedStartDate: string, plannedEndDate: string}} payload
     * @returns {Promise<{data: ProductionOrderResponseDto}>}
     */
    planOrder(id, payload) {
      return instance.post(`${BASE_PATH}/${id}/plan`, payload);
    },

    /**
     * Start a planned production order - moves it to Released and releases
     * WIP for every active line
     * @param {number} id
     * @returns {Promise<{data: {order: ProductionOrderResponseDto, lineResults: Array<{orderLineId: number, success: boolean, errorCode?: string, errorMessage?: string}>}}>}
     */
    startOrder(id, { fulfillFromInventory = false, fulfillSemifinishedFromInventory = false } = {}) {
      return instance.post(`${BASE_PATH}/${id}/start`, null, { params: { fulfillFromInventory, fulfillSemifinishedFromInventory } });
    },

    /**
     * Preview how much of each active line's own component is currently
     * available in inventory, for the Start-time fulfill-from-inventory prompt
     * @param {number} id
     * @returns {Promise<{data: Array<{orderLineId: number, componentId: number, componentName: string, componentCode: string, quantityOrdered: number, availableQuantity: number, warehouseName: string}>}>}
     */
    getInventoryAvailability(id) {
      return instance.get(`${BASE_PATH}/${id}/inventory-availability`);
    },

    /**
     * Preview which semifinished/intermediate route steps have warehouse
     * stock available, for the Start-time fulfill-semifinished-from-inventory
     * prompt (a second, independent question from getInventoryAvailability's
     * final-component one)
     * @param {number} id
     * @returns {Promise<{data: Array<{orderLineId: number, routeStepId: number, stepName: string, componentId: number, componentName: string, componentCode: string, availableQuantity: number, warehouseName: string}>}>}
     */
    getSemifinishedAvailability(id) {
      return instance.get(`${BASE_PATH}/${id}/semifinished-availability`);
    },

    /**
     * Complete a released or in-progress production order and settle its
     * finished-output inventory
     * @param {number} id
     * @returns {Promise<{data: ProductionOrderResponseDto}>}
     */
    completeOrder(id) {
      return instance.post(`${BASE_PATH}/${id}/complete`);
    },

    /**
     * Ship a completed or packed production order
     * @param {number} id
     * @returns {Promise<{data: ProductionOrderResponseDto}>}
     */
    shipOrder(id) {
      return instance.post(`${BASE_PATH}/${id}/ship`);
    },

    /**
     * Get production order lines
     * @param {number} orderId
     * @returns {Promise<{data: ProductionOrderLineResponseDto[]}>}
     */
    getLines(orderId) {
      return instance.get(`${BASE_PATH}/${orderId}/lines`);
    },

    /**
     * Add line to production order
     * @param {number} orderId
     * @param {ProductionOrderLineCreateDto} payload
     * @returns {Promise<{data: ProductionOrderLineResponseDto}>}
     */
    createLine(orderId, payload) {
      return instance.post(`${BASE_PATH}/${orderId}/lines`, payload);
    },

    /**
     * Update production order line
     * @param {number} orderId
     * @param {number} lineId
     * @param {ProductionOrderLineUpdateDto} payload
     * @returns {Promise<{data: ProductionOrderLineResponseDto}>}
     */
    updateLine(orderId, lineId, payload) {
      return instance.put(`${BASE_PATH}/${orderId}/lines/${lineId}`, payload);
    },

    /**
     * Delete production order line (soft delete)
     * @param {number} orderId
     * @param {number} lineId
     * @returns {Promise<void>}
     */
    deleteLine(orderId, lineId) {
      return instance.delete(`${BASE_PATH}/${orderId}/lines/${lineId}`);
    },

    /**
     * Add a component to a custom line's ad-hoc component list
     * @param {number} orderId
     * @param {number} lineId
     * @param {{componentId: number, quantity: number}} payload
     * @returns {Promise<{data: CustomLineComponentDto}>}
     */
    addCustomComponent(orderId, lineId, payload) {
      return instance.post(`${BASE_PATH}/${orderId}/lines/${lineId}/custom-components`, payload);
    },

    /**
     * Remove a component from a custom line's ad-hoc component list
     * @param {number} orderId
     * @param {number} lineId
     * @param {number} customLineComponentId
     * @returns {Promise<void>}
     */
    deleteCustomComponent(orderId, lineId, customLineComponentId) {
      return instance.delete(`${BASE_PATH}/${orderId}/lines/${lineId}/custom-components/${customLineComponentId}`);
    },

    /**
     * Get delete preview (counts of child records to be removed)
     * @param {number} id
     * @returns {Promise<{data: {orderNumber: string, lineCount: number, wipItemCount: number, stepExecCount: number, outputCount: number, birthCfgCount: number}}>}
     */
    getDeletePreview(id) {
      return instance.get(`${BASE_PATH}/${id}/delete-preview`);
    },

    /**
     * Get all production order statuses
     * @returns {Promise<{data: ProductionOrderStatusDto[]}>}
     */
    getStatuses() {
      return instance.get(`${BASE_PATH}/statuses`);
    },

    /**
     * Get a suggested, currently-unused order number
     * @param {Object} [params]
     * @param {boolean} [params.isInternal=false] - PLN- prefix for Planned/internal orders instead of ORD-
     * @returns {Promise<{data: {orderNumber: string}}>}
     */
    getNextOrderNumber(params = {}) {
      return instance.get(`${BASE_PATH}/next-order-number`, { params });
    },

    /**
     * Rank active boxes by fit for this order's line items
     * @param {number} orderId
     * @returns {Promise<{data: SuggestedBoxDto[]}>}
     */
    getSuggestedBox(orderId) {
      return instance.get(`${BASE_PATH}/${orderId}/suggested-box`);
    },

    /**
     * Pack a completed order into a box - consumes the box's stock and
     * moves the order to Packed
     * @param {number} orderId
     * @param {{boxId: number, quantity?: number, notes?: string}} payload
     * @returns {Promise<{data: OrderPackingResponseDto[]}>}
     */
    packOrder(orderId, payload) {
      return instance.post(`${BASE_PATH}/${orderId}/pack`, payload);
    },

    /**
     * Get packing history for this order
     * @param {number} orderId
     * @returns {Promise<{data: OrderPackingResponseDto[]}>}
     */
    getPackingHistory(orderId) {
      return instance.get(`${BASE_PATH}/${orderId}/packing`);
    }
  };
}
